// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/app/components/layout/pioneer/ThemeModeSwitch.module.css';
import * as ThemeProfileCommands from '@app/features/theme/commands/ThemeProfileCommands';
import ThemeEditTarget from '@app/features/theme/state/ThemeEditTarget';
import ThemeProfile from '@app/features/theme/state/ThemeProfile';
import {type ThemeType, ThemeTypes} from '@fluxer/constants/src/UserConstants';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {DesktopIcon, MoonIcon, SunIcon} from '@phosphor-icons/react';
import clsx from 'clsx';
import {observer} from 'mobx-react-lite';

const APPEARANCE = msg({message: 'Appearance', comment: 'Label of the Light / Dark / Auto switch.'});
const LIGHT = msg({message: 'Light', comment: 'Light mode option.'});
const DARK = msg({message: 'Dark', comment: 'Dark mode option.'});
const AUTO = msg({message: 'Auto', comment: 'Follow the device light or dark setting.'});

const OPTIONS = [
	{theme: ThemeTypes.LIGHT, label: LIGHT, icon: SunIcon},
	{theme: ThemeTypes.DARK, label: DARK, icon: MoonIcon},
	{theme: ThemeTypes.SYSTEM, label: AUTO, icon: DesktopIcon},
] as const;

/** One-tap Light / Dark / Auto. Dark covers the other dark themes (Coal, legacy). */
export const ThemeModeSwitch = observer(function ThemeModeSwitch({compact = false}: {compact?: boolean}) {
	const {i18n} = useLingui();
	const target = ThemeEditTarget.target;
	const current = ThemeProfile.themePreferenceFor(target);
	const active: ThemeType =
		current === ThemeTypes.LIGHT || current === ThemeTypes.SYSTEM ? current : ThemeTypes.DARK;
	return (
		<div className={clsx(styles.root, compact && styles.compact)} data-flx="app.pioneer.theme-mode-switch.root">
			<span className={styles.label} id="theme-mode-switch-label" data-flx="app.pioneer.theme-mode-switch.label">
				{i18n._(APPEARANCE)}
			</span>
			<div
				className={styles.segmented}
				role="radiogroup"
				aria-labelledby="theme-mode-switch-label"
				data-flx="app.pioneer.theme-mode-switch.segmented"
			>
				{OPTIONS.map((option) => {
					const Icon = option.icon;
					const on = active === option.theme;
					return (
						<button
							key={option.theme}
							type="button"
							role="radio"
							aria-checked={on}
							className={clsx(styles.option, on && styles.optionOn)}
							onClick={(event) => {
								event.stopPropagation();
								if (current !== option.theme) ThemeProfileCommands.setThemePreference(option.theme, target);
							}}
							data-flx="app.pioneer.theme-mode-switch.option.set-theme.button"
						>
							<Icon size={16} weight={on ? 'fill' : 'regular'} aria-hidden="true" data-flx="app.pioneer.theme-mode-switch.icon" />
							{i18n._(option.label)}
						</button>
					);
				})}
			</div>
		</div>
	);
});
